import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useAccounts,
  useProducts,
  useTrainers,
  useTrainingVocabularies,
  useCreateTrainingSession,
} from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { useDebounce } from '@/hooks/use-debounce'
import { explainError } from '@/lib/explain-error'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Record a training session that no deal created.
 *
 * The spreadsheet let anyone type in a row, and things genuinely arrive that way:
 * free sessions, webinars, and the ~214 training lines sold on closed deals that
 * the sheet never tracked. Without this the worklist can only ever hold what a
 * Closed Won deal or the one-time import produced.
 */
export function NewTrainingSessionDialog({ open, onOpenChange }: Props) {
  const { toast } = useToast()
  const [accountSearch, setAccountSearch] = useState('')
  const debouncedAccount = useDebounce(accountSearch, 300)

  const [accountId, setAccountId] = useState('')
  const [courseName, setCourseName] = useState('')
  const [trainerName, setTrainerName] = useState('')
  const [billedFree, setBilledFree] = useState('')
  const [duration, setDuration] = useState('')

  const { data: accountsResp } = useAccounts({
    search: debouncedAccount || undefined,
    limit: 25,
  })
  // Only training products — a session for "Grackle PDF License" would be nonsense.
  const { data: productsResp } = useProducts({ limit: 10000 })
  const { data: vocabResp } = useTrainingVocabularies()
  const { data: trainerResp } = useTrainers()
  const create = useCreateTrainingSession()

  const accounts = accountsResp?.data ?? []
  const courses = (productsResp?.data ?? [])
    .filter((p) => (p.family ?? '').trim() === 'Grackle Training')
    .map((p) => p.name)
  const vocab = vocabResp?.data
  const trainers = (trainerResp?.data ?? []).map((t) => t.name)

  const reset = () => {
    setAccountId('')
    setCourseName('')
    setTrainerName('')
    setBilledFree('')
    setDuration('')
    setAccountSearch('')
  }

  const submit = async () => {
    if (!accountId || !courseName) return
    try {
      await create.mutateAsync({
        account_id: accountId,
        course_name: courseName,
        trainer_name: trainerName || null,
        billed_free: billedFree || null,
        duration_text: duration || null,
        status: 'Not started',
      })
      toast({ title: 'Session added', description: courseName })
      reset()
      onOpenChange(false)
    } catch (err) {
      toast({
        title: 'Could not add the session',
        description: explainError(err, 'Please try again.'),
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New training session</DialogTitle>
          <DialogDescription>
            For training that no closed deal produced — a free session, a webinar, or one that was
            never tracked.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="new-session-account" className="required">
              Client *
            </Label>
            <Input
              id="new-session-account"
              value={accountSearch}
              onChange={(e) => setAccountSearch(e.target.value)}
              placeholder="Search for an account..."
              aria-describedby="new-session-account-help"
            />
            <p id="new-session-account-help" className="text-xs text-muted-foreground">
              Search, then choose below.
            </p>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger aria-label="Choose the client account">
                <SelectValue placeholder="Choose an account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-session-course" className="required">
              Training type *
            </Label>
            <Select value={courseName} onValueChange={setCourseName}>
              <SelectTrigger id="new-session-course">
                <SelectValue placeholder="Choose a course" />
              </SelectTrigger>
              <SelectContent>
                {courses.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-session-trainer">Trainer</Label>
              <Select value={trainerName} onValueChange={setTrainerName}>
                <SelectTrigger id="new-session-trainer">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  {trainers.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-session-billed">Billed/Free</Label>
              <Select value={billedFree} onValueChange={setBilledFree}>
                <SelectTrigger id="new-session-billed">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {(vocab?.billed_free ?? []).map((v) => (
                    <SelectItem key={v} value={v}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-session-duration">Duration</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger id="new-session-duration">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {(vocab?.duration ?? []).map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!accountId || !courseName || create.isPending}>
              Add session
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
